import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SEO_PAGES } from "@/data/seoPages";
import { getPost } from "@/data/blogPosts";
import { IMAGES, LOGO } from "@/lib/constants";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";

const HERO_IMG = {
  "visa-australie": IMAGES.australia,
  "travailler-en-australie": IMAGES.australia,
  "guide-australie": IMAGES.australia,
  "visa-australie-depuis-la-france": IMAGES.australia,
  "accompagnement-visa-australie": IMAGES.australia,
  "visa-canada": IMAGES.canada,
  "immigration-canada": IMAGES.canada,
  "pvt-canada": IMAGES.canada,
  "travailler-au-canada": IMAGES.canada,
  "accompagnement-visa-canada": IMAGES.canada,
};

export default function SeoPage() {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const hero = HERO_IMG[slug] || IMAGES.passport;
  const url = `https://worktravelexpert.net/${slug}`;
  const blogLinks = (page.relatedBlog || []).map(getPost).filter(Boolean);

  const schema = [
    breadcrumbSchema([
      { name: page.overline.replace(/[^\wÀ-ÿ ]/g, "").trim(), to: page.overline.includes("🇨🇦") ? "/visa-canada" : "/visa-australie" },
      { name: page.h1, to: `/${slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.h1,
      description: page.metaDescription,
      image: hero,
      inLanguage: "fr-FR",
      keywords: page.keywords,
      author: { "@type": "Organization", name: "Work & Travel Expert" },
      publisher: {
        "@type": "Organization",
        name: "Work & Travel Expert",
        logo: { "@type": "ImageObject", url: LOGO },
      },
      mainEntityOfPage: url,
    },
  ];

  if (page.faqs?.length) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <div data-testid={`seo-page-${slug}`}>
      <Seo
        title={page.title}
        description={page.metaDescription}
        keywords={page.keywords}
        path={`/${slug}`}
        image={hero}
        type="article"
        schema={schema}
      />

      {/* HERO */}
      <div className="relative bg-navy">
        <img src={hero} alt={page.h1} loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-navy/70" />
        <Section className="relative z-10 py-24">
          <Breadcrumbs theme="dark" items={[{ name: page.overline.replace(/[^\wÀ-ÿ ]/g, "").trim(), to: page.overline.includes("🇨🇦") ? "/visa-canada" : "/visa-australie" }, { name: page.h1 }]} />
          <div className="mt-5" />
          <Overline>{page.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white max-w-3xl leading-[1.1]">{page.h1}</h1>
          <p className="mt-6 text-white/80 max-w-2xl leading-relaxed">{page.intro}</p>
          <Link to="/nos-packs" data-testid="seo-hero-cta" className="group inline-flex items-center gap-2 mt-8 bg-gold hover:bg-gold-hover text-white px-8 py-3.5 font-medium transition-colors">
            Évaluer mon éligibilité <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </Section>
      </div>

      {/* CONTENU */}
      <Section className="py-20 max-w-3xl">
        <article className="space-y-14">
          {page.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <section data-testid={`seo-section-${i}`}>
                <h2 className="font-heading text-2xl sm:text-3xl text-navy">{s.h}</h2>
                <div className="gold-line mt-5" />
                <p className="mt-6 text-slate-700 leading-relaxed">{s.p}</p>
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((li, k) => (
                      <li key={k} className="flex items-start gap-3 text-slate-700">
                        <CheckCircle2 size={20} className="text-gold shrink-0 mt-0.5" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}
        </article>

        {/* FAQ */}
        {page.faqs?.length > 0 && (
          <div className="mt-16" data-testid="seo-faq">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Questions fréquentes</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-navy mt-3">Vos questions sur {page.overline.replace(/[^\wÀ-ÿ ]/g, "").trim()}</h2>
            <div className="gold-line mt-5" />
            <Accordion type="single" collapsible className="w-full mt-6">
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`seo-faq-item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-heading text-lg text-navy hover:text-gold hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-700 leading-relaxed text-base pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 bg-navy text-white p-8 sm:p-10">
          <h2 className="font-heading text-2xl sm:text-3xl">Prêt à concrétiser votre projet ?</h2>
          <p className="mt-4 text-white/75 leading-relaxed">Découvrez nos formules d'accompagnement et bénéficiez d'un accompagnement personnalisé jusqu'à la finalisation de votre démarche.</p>
          <Link to="/nos-packs" data-testid="seo-cta-packs" className="group inline-flex items-center gap-2 mt-6 bg-gold hover:bg-gold-hover text-white px-8 py-3.5 font-medium transition-colors">
            Voir nos packs <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* GUIDES DU BLOG (maillage interne) */}
        {blogLinks.length > 0 && (
          <div className="mt-16">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Nos guides détaillés</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              {blogLinks.map((b) => (
                <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`seo-blog-${b.slug}`} className="group block border border-border bg-white hover:border-gold transition-colors">
                  <div className="h-32 overflow-hidden">
                    <img src={b.image} alt={b.h1} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs uppercase tracking-widest font-semibold text-gold">{b.tags[0]}</span>
                    <h3 className="font-heading text-base text-navy mt-2 leading-snug group-hover:text-gold transition-colors">{b.h1}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* MAILLAGE INTERNE — pages SEO */}
        {page.related?.length > 0 && (
          <div className="mt-16">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">À lire également</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-3">
              {page.related.map((rs) => {
                const r = SEO_PAGES[rs];
                if (!r) return null;
                return (
                  <Link
                    key={rs}
                    to={`/${rs}`}
                    data-testid={`seo-related-${rs}`}
                    className="group block border border-border bg-white p-6 hover:border-gold transition-colors"
                  >
                    <span className="text-xs uppercase tracking-widest font-semibold text-gold">{r.overline}</span>
                    <h3 className="font-heading text-lg text-navy mt-2 leading-snug group-hover:text-gold transition-colors">{r.h1}</h3>
                    <span className="inline-flex items-center gap-1 mt-4 text-sm text-navy group-hover:gap-2 transition-all">
                      Lire l'article <ArrowRight size={15} />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
